import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  DatatableComponent,
  NgxDatatableModule,
  SelectionType,
} from '@swimlane/ngx-datatable'
import { BehaviorSubject } from 'rxjs'
import { InputHelper } from 'src/helpers/input.helper'
import { Utils } from 'src/helpers/utils.helper'

import { Store } from '@ngxs/store'
import { LayoutMetaState } from 'src/store/layout/layout.meta.state'
import {
  ITableAction,
  ITableActionIcon,
  ITableBadge,
  ITableFilter,
  ITableHeader,
  SortDirection,
} from '../../../../interfaces/table/table.interface'
import { CommonModule } from '@angular/common'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslateModule } from '@ngx-translate/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  standalone: true,
  imports: [
    CommonModule,
    NgxDatatableModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
  ],
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {
  private utils = inject(Utils)
  private inputHelper = inject(InputHelper)
  private store = inject(Store)
  private destroyRef = inject(DestroyRef)
  private isSidebarOpen$ = this.store.select(LayoutMetaState.getIsSidebarOpen)
  /**
   * Created in the expectation of receiving shares from the table dynamically
   *
   * @type {BehaviorSubject<string[]>}
   * @memberof TableComponent
   */
  currentActions!: BehaviorSubject<string[]>

  selected: any[] = []
  selectionType = SelectionType

  codeImg: string = 'assets/images/Kit1.png'

  @Input() tableData!: any[]
  @Input() staticTableData!: any[]
  @Input() tableHeaders!: ITableHeader[]
  @Input() tableFilters!: ITableFilter[]
  @Input() tableOffset: number = 0
  @Input() tableLimit: number = 10
  @Input() tableCount!: number
  @Input() externalPaging: boolean = false
  @Input() tableActionIcons!: ITableActionIcon[]
  @Input() tableBadges!: ITableBadge[]
  @Input() tableIcons!: ITableBadge[]
  @Input() allowHandleQuantityOfMultipleItems: boolean = true

  @Output() tableAction = new EventEmitter<ITableAction>()

  @ViewChild('table') table!: DatatableComponent

  scrollBarHorizontal = window.innerWidth < 1400

  constructor() {
    window.onresize = () => {
      this.scrollBarHorizontal = window.innerWidth < 1400
    }
  }

  ngOnInit(): void {
    this.currentActions = new BehaviorSubject<string[]>([])
    this.isSidebarOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resizeTable()
      })
  }

  ngOnChanges(): void {
    if (this.staticTableData?.length > 0) {
      if (this.tableFilters?.length === 0) {
        this.clearFilters()
      } else {
        if (this.tableFilters?.length > 0) {
          this.filterTableData(this.tableFilters)
        }
      }
    }
  }

  showImageOnMouseEnter(event: Event): void {
    const element = event.target as HTMLElement
    element.classList.add('show-img')
  }
  removeImageOnMouseLeave(event: Event): void {
    const element = event.target as HTMLElement
    element.classList.remove('show-img')
  }

  /**
   * Filter table data based on the filters
   * @param filters
   */
  filterTableData(filters: ITableFilter[]): void {
    this.tableData = this.tableData.filter((element: any) => {
      return filters.every((filter: ITableFilter) => {
        return element[filter.columnDef] === filter.value
      })
    })
  }

  filterData(value: string) {
    if (value !== '') {
      const valueLowerCase = value.toString().toLowerCase().trim()
      this.tableData = this.staticTableData.filter((item) => {
        if (
          (item.equipment &&
            item.equipment.toString().toLowerCase().indexOf(valueLowerCase) !==
              -1) ||
          !value
        ) {
          return true
        }
      })
    } else {
      this.tableData = this.staticTableData
    }
  }

  clearFilters(): void {
    this.tableData = this.staticTableData
  }

  displayCheck(): boolean {
    return true
  }

  /**
   * Update table action based on the selected rows and the actions
   * @param event
   */
  onSelect(event: any): void {
    const data = event.selected
    const currentActionsSnapshot = this.currentActions.getValue()
    const foundSelectedAction = currentActionsSnapshot.filter(
      (action) => action === 'selected',
    )[0]
    const foundSelectedActionIndex =
      currentActionsSnapshot.indexOf(foundSelectedAction)

    if (data && data.length > 0) {
      if (!foundSelectedAction) {
        currentActionsSnapshot.push('selected')
      }
    } else {
      currentActionsSnapshot.splice(foundSelectedActionIndex, 1)
    }

    this.currentActions.next(currentActionsSnapshot)
    const updatedCurrentActions = this.currentActions.getValue()

    const selectedRowsIndexes = event.selected.map((row: any) => {
      return this.tableData.indexOf(row)
    })
    this.onUpdateTableAction(
      event.selected,
      updatedCurrentActions,
      selectedRowsIndexes,
    )
  }

  /**
   * Set selected rows based on clicked row
   *
   * @param {string} action
   * @param {*} row
   * @memberof TableComponent
   */
  onClickIconAction(action: string, row: any): void {
    this.selected = [row]
    const foundIndexOfRow = this.tableData.indexOf(row)
    this.onUpdateTableAction(this.selected, [action], [foundIndexOfRow])
  }

  onClickVideo() {
    console.log('Video')
  }

  /**
   * Update table action based on the selected rows and the actions
   *
   * @param {any[]} data
   * @param {string[]} actions
   * @memberof TableComponent
   */
  onUpdateTableAction(
    data: any[],
    actions: string[],
    rowsIndexes: number[],
  ): void {
    this.tableAction.emit({
      actions,
      data,
      rowsIndexes,
    })
  }

  onClickCheckboxAction(action: string, row: any, checked: boolean): void {
    this.selected = [row]
    const foundIndexOfRow = this.tableData.indexOf(row)
    this.onUpdateTableWithCheckboxAction(
      this.selected,
      [action],
      [foundIndexOfRow],
      checked,
    )
  }

  onUpdateTableWithCheckboxAction(
    data: any[],
    actions: string[],
    rowsIndexes: number[],
    checked: boolean,
  ): void {
    this.tableAction.emit({
      actions,
      data,
      rowsIndexes,
      checked,
    })
  }

  /**
   * Created expecting to receive pagination information from the table
   *
   * @param {*} event
   * @memberof TableComponent
   */
  onPaginate(event: any): void {
    this.table.recalculate()
    this.table.ngDoCheck()
    this.tableAction.emit({
      actions: ['paginate'],
      offset: event.offset + 1,
      data: [],
      rowsIndexes: [],
    })
  }

  onSort(event: any): void {
    this.table.recalculate()
    this.table.ngDoCheck()
    this.tableAction.emit({
      actions: ['sort'],
      data: [],
      rowsIndexes: [],
      sortOptions: {
        columnDef: event.column.prop,
        sortDirection:
          event.newValue === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      },
    })
  }

  getBadgeClass(badge: ITableBadge, value: string | boolean): string {
    const foundValue = badge.values.filter(
      (badgeValue) => badgeValue.value === value,
    )[0]
    return foundValue ? foundValue.class : badge?.values[0]?.class || ''
  }

  getIconName(icon: ITableBadge, value: string | boolean): string {
    const foundValue = icon.values.filter(
      (badgeValue) => badgeValue.value === value,
    )[0]
    return foundValue ? foundValue.iconName : icon?.values[0]?.iconName || ''
  }

  getBadgeValue(badge: ITableBadge, value: string | boolean): string {
    const foundValue = badge.values.filter(
      (badgeValue) => badgeValue.value === value,
    )[0]
    if (foundValue) {
      return foundValue.parsedValue
        ? foundValue.parsedValue
        : foundValue?.value?.toString() || value.toString()
    }
    return value.toString()
  }

  increaseQuantity(row: any): void {
    row.quantity++
    this.updateAmount(row)
    this.tableAction.emit({
      actions: ['quantity'],
      data: [row],
      rowsIndexes: [this.tableData.indexOf(row)],
    })
  }

  decreaseQuantity(row: any): void {
    if (row.quantity > 0) {
      row.quantity--
      this.updateAmount(row)
      this.tableAction.emit({
        actions: ['quantity'],
        data: [row],
        rowsIndexes: [this.tableData.indexOf(row)],
      })
    } else {
      this.updateAmount(row)
    }
  }

  updateAmount(row: any): void {
    if (row.amount && row.value) {
      if (row.quantity === 0) {
        row.amount = this.utils.parseValueToBRLCurrency(0)
      } else {
        const parsedValue = this.utils.parseBRLCurrencyToValue(row.value)
        row.amount = this.utils.parseValueToBRLCurrency(
          row.quantity * parsedValue,
        )
      }
    }
  }

  changeQuantity(row: any, event: any): void {
    row.quantity = Number(event.target.value)
    this.updateAmount(row)
    this.tableAction.emit({
      actions: ['quantity'],
      data: [row],
      rowsIndexes: [this.tableData.indexOf(row)],
    })
  }

  callCheckLength(e, input) {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.target.blur()
    }
    this.inputHelper.checkLength(e, input, 99999)
  }

  mustDisableQuantityField(row: any): boolean {
    const foundItemWithQuantity = this.tableData.filter(
      (item) => item.quantity > 0,
    )[0]
    return (
      foundItemWithQuantity &&
      foundItemWithQuantity !== row &&
      !this.allowHandleQuantityOfMultipleItems
    )
  }

  private resizeTable() {
    if (this.table) {
      setTimeout(() => {
        this.table.recalculate()
        this.table.recalculateColumns()
      }, 300)
    }
  }
}
